import type { Feature, PermissionLevel, Role } from '@/enum/global.enum'
import { useActiveOrg } from '@/hooks/use-active-org'
import { useOrganisationModules } from '@/hooks/use-organisation-modules'
import {
  getUserFeaturePermissionLevel,
  hasAllRequiredRoles,
  hasAnyRequiredRole,
  hasNavigationPermission,
  hasRequiredFeature,
  hasRequiredRole,
  type PermissionCheckOptions,
} from '@/utils/permission.utils'
import { isOrgRole } from '@/utils/role-check.utils'
import { useMemo } from 'react'

export const usePermissions = () => {
  const { activeOrg } = useActiveOrg()
  const { isFeatureEnabledForOrg, isLoading: isOrganisationModulesLoading } = useOrganisationModules()

  const permissions = useMemo(
    () => ({
      /**
       * Check if user has required role
       */
      hasRole: (requiredRoles: Role[] | undefined): boolean => {
        return hasRequiredRole(activeOrg?.role, requiredRoles)
      },

      /**
       * Check if user has any of the required roles
       */
      hasAnyRole: (requiredRoles: Role[] | undefined): boolean => {
        return hasAnyRequiredRole(activeOrg?.role, requiredRoles)
      },

      /**
       * Check if user has all required roles
       */
      hasAllRoles: (requiredRoles: Role[] | undefined): boolean => {
        return hasAllRequiredRoles(activeOrg?.role, requiredRoles)
      },

      /**
       * Check if user has required feature permission
       */
      hasFeature: (requiredFeature: Feature | undefined, options: PermissionCheckOptions = {}): boolean => {
        return hasRequiredFeature(activeOrg?.featurePermissions, requiredFeature, options)
      },

      canView: (feature: Feature | undefined): boolean => {
        return hasRequiredFeature(activeOrg?.featurePermissions, feature, { requireViewPermission: true })
      },

      canAdd: (feature: Feature | undefined): boolean => {
        return hasRequiredFeature(activeOrg?.featurePermissions, feature, { requireAddPermission: true })
      },

      canEdit: (feature: Feature | undefined): boolean => {
        return hasRequiredFeature(activeOrg?.featurePermissions, feature, { requireEditPermission: true })
      },

      canDelete: (feature: Feature | undefined): boolean => {
        return hasRequiredFeature(activeOrg?.featurePermissions, feature, { requireDeletePermission: true })
      },

      /**
       * Comprehensive navigation permission check.
       * For org users, also ensures the feature's module is enabled for the organisation (onboarding selection).
       */
      canNavigate: (
        requiredRole: Role[] | undefined,
        requiredFeature: Feature | undefined,
        permissionOptions: PermissionCheckOptions = {}
      ): boolean => {
        const hasBasePermission = hasNavigationPermission(activeOrg, requiredRole, requiredFeature, permissionOptions)
        if (!hasBasePermission) return false

        // For org users: only allow features whose module is enabled for this organisation
        if (requiredFeature && activeOrg?.role && isOrgRole(activeOrg.role)) {
          if (isOrganisationModulesLoading) return true // allow while loading to avoid flash
          if (!isFeatureEnabledForOrg(requiredFeature)) return false
        }

        return true
      },

      /**
       * Get user's permission level for a specific feature
       */
      getFeaturePermissionLevel: (feature: Feature): PermissionLevel | undefined => {
        return getUserFeaturePermissionLevel(activeOrg?.featurePermissions, feature)
      },

      /**
       * Check if user has any permission for a feature (any level)
       */
      hasAnyFeaturePermission: (feature: Feature | undefined): boolean => {
        return hasRequiredFeature(activeOrg?.featurePermissions, feature)
      },

      /**
       * Get all user's feature permissions
       */
      getAllFeaturePermissions: () => {
        return activeOrg?.featurePermissions || []
      },

      /**
       * Get user's current role
       */
      getCurrentRole: (): Role | string | undefined => {
        return activeOrg?.role
      },

      /**
       * Check if user is authenticated and has an active organisation
       */
      isAuthenticated: (): boolean => {
        return !!activeOrg
      },
    }),
    [activeOrg, isFeatureEnabledForOrg, isOrganisationModulesLoading]
  )

  return permissions
}

/**
 * Hook for checking specific navigation permissions
 */
export const useNavigationPermissions = (
  requiredRole: Role[] | undefined,
  requiredFeature: Feature | undefined,
  permissionOptions: PermissionCheckOptions = {}
) => {
  const { canNavigate } = usePermissions()

  return useMemo(
    () => ({
      canAccess: canNavigate(requiredRole, requiredFeature, permissionOptions),
      requiredRole,
      requiredFeature,
      permissionOptions,
    }),
    [canNavigate, requiredRole, requiredFeature, permissionOptions]
  )
}

/**
 * Hook for checking feature-specific permissions
 */
export const useFeaturePermissions = (feature: Feature | undefined) => {
  const { canView, canAdd, canEdit, canDelete, getFeaturePermissionLevel } = usePermissions()

  return useMemo(
    () => ({
      canView: canView(feature),
      canAdd: canAdd(feature),
      canEdit: canEdit(feature),
      canDelete: canDelete(feature),
      permissionLevel: getFeaturePermissionLevel(feature!),
      hasAnyPermission: canView(feature) || canAdd(feature) || canEdit(feature) || canDelete(feature),
    }),
    [canView, canAdd, canEdit, canDelete, getFeaturePermissionLevel, feature]
  )
}
